/*!
 * Omneo Lists — Zapier Sync Tracking (temporary, retirable)
 * -------------------------------------------------------------------------
 * Replaces the old hardcoded Zapier webhook call in omneo-lists.liquid,
 * which only ever tracked Reserve List ADDS specifically (buried inside
 * the legacy reserve-list click handler), had no retry/idempotency, and
 * silently swallowed its own fetch failures (fire-and-forget, no error
 * handling on the webhook call itself).
 *
 * This was originally built to troubleshoot inconsistent customer
 * sync experiences during the Phase 1 rebuild. Per discussion when this
 * was scoped: keep Zapier as the receiving end, but rewire the trigger
 * onto this package's own sync:success/sync:failed events instead of
 * the old narrow hook — covers BOTH list types and BOTH add/remove,
 * not just reserve-list adds. Intended as a migration-period safety
 * net, not a permanent feature — plan to retire this file (and the
 * Zapier zap) after a defined window of clean data post-rollout, per
 * that same discussion. Deleting this file and its render line in
 * omneo-lists-init.liquid is a clean removal, same as any other
 * optional piece of this package.
 *
 * TRACKS FAILURES ONLY BY DEFAULT (sync:failed) — briefly changed to
 * track both successes and failures, then reverted back per a cost
 * concern (Zapier's task-based pricing). Set
 * window.OmneoListsZapierConfig.trackSuccess = true (before this
 * script runs) to also send sync:success events if that's ever wanted
 * again — the readable format (requestId, customerId, itemID, product
 * title, timestamp, combined status) still applies either way.
 *
 * One intentional difference from the old format: the old code logged
 * two separate booleans (Omneo-login-state, Shopify-login-state) —
 * that dual-check made sense for its specific architecture (the iframe
 * portal, where those two states could genuinely disagree, see
 * omneo-lists.js's stale-login-recovery fix for why). This package's
 * config.isLoggedIn is derived directly from Shopify's own customer
 * object at render — a single flag is the accurate equivalent here,
 * not a second, effectively-always-matching boolean.
 *
 * Requires: window.OmneoLists (omneo-lists.js, loaded first).
 * -------------------------------------------------------------------------
 */
(function (OmneoLists) {
  'use strict';

  if (!OmneoLists) return;

   var userConfig = window.OmneoListsZapierConfig || {};

  // Easy on/off switch — set window.OmneoListsZapierConfig.enabled =
  // false (before this script runs, e.g. in omneo-lists-init.liquid)   
  // to turn tracking off entirely without removing the script tag or
  // deleting this file. Defaults to on.
  if (userConfig.enabled === false) return;

  var WEBHOOK_URL = 'https://hooks.zapier.com/hooks/catch/26205319/uq7vbg1/';

  // ⚠ ASSUMPTION, not confirmed: reusing the SAME Zapier catch-hook URL
  // the old code used. If the existing Zap's field-mapping step expects
  // ONLY the old field names and errors on unexpected extra/renamed
  // fields, the new fields below may need the Zap itself updated to map
  // them — that's outside this file's control. Worth a test send + a
  // check in Zapier's dashboard before relying on this in production.
  // If a fresh Zap/URL is preferred instead of reusing this one, swap
  // the constant above.

  var trackSuccess = !!userConfig.trackSuccess; // default false — failures only, per cost concern

  
  // requestId here is just a client-generated correlation ID for
  // matching related events in Zapier's history — NOT the same concept
  // as the old code's data-request-id (which came from a DOM attribute
  // whose origin wasn't traced as part of this rewiring; not needed for
  // this purpose, a fresh ID per sync attempt is sufficient).
  function generateRequestId() {
    return 'sync-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8);
  }

  // Readable list-type labels for the combined status field below.
  var LIST_TYPE_LABELS = {
    wish_list: 'Wishlist',
    reserve_list: 'Reserve List',
    recommended_list: 'Recommended'
  };

  var ACTION_LABELS = {
    add: 'Add',
    remove: 'Remove'
  };

  // Distinct from config.isLoggedIn (Shopify's own customer object,
  // server-rendered) — this tracks whether Omneo's own auth flow
  // actually completed successfully. The two can genuinely differ: a
  // customer can be logged into Shopify while Omneo's token
  // exchange/auth fails (network issue, /apps/cx problem, etc.), which
  // is exactly the kind of thing worth being able to see in this log
  // per request.
  var omneoLoggedIn = false;
  OmneoLists.on('auth:ready', function () { omneoLoggedIn = true; });
  OmneoLists.on('auth:failed', function () { omneoLoggedIn = false; });

  function sendToZapier(status, detail) {
    var config = OmneoLists._getConfig();
    var listTypeLabel = LIST_TYPE_LABELS[detail.type] || detail.type || '';
    var actionLabel = ACTION_LABELS[detail.action] || detail.action || '';
    // Combined per request — e.g. "Wishlist Add Synced" / "Reserve
    // List Remove Failed" — readable at a glance without cross-
    // referencing the separate listType/action columns.
    var combinedStatus = [listTypeLabel, actionLabel, status.charAt(0).toUpperCase() + status.slice(1)]
      .filter(Boolean).join(' ');
    var body = new URLSearchParams({
      requestId: generateRequestId(),
      customerId: (config && config.customerId) || '',
      isLoggedIn: (config && config.isLoggedIn) ? 'TRUE' : 'FALSE',
      omneoLoggedIn: omneoLoggedIn ? 'TRUE' : 'FALSE',
      itemID: detail.variantId || '',
      sku: detail.sku || '',
      productTitle: detail.title || '',
      listType: detail.type || '',
      action: detail.action || '',
      status: combinedStatus, // e.g. "Wishlist Add Synced" — was plain 'synced'/'failed'
      error: detail.error ? String(detail.error.message || detail.error) : '',
      timestamp: new Date().toISOString()
    });

    // Fire-and-forget, matching the old code's behaviour — but at least
    // logged on failure now, rather than silently swallowed.
    fetch(WEBHOOK_URL, { method: 'POST', body: body })
      .catch(function (err) {
        console.warn('[OmneoLists Zapier Tracking] Webhook call itself failed.', err);
      });
  }

  OmneoLists.on('sync:failed', function (detail) {
    sendToZapier('failed', detail);
  });

  if (trackSuccess) {
    OmneoLists.on('sync:success', function (detail) {
      sendToZapier('synced', detail);
    });
  }

})(window.OmneoLists);